import React from 'react';
import { getPhotoUrl } from '../../api/photos';
import { Gender, BirdStatus } from '../../utils/enums';
import { getGenderAvatarClass, getGenderColorClass, getGenderIcon } from '../../utils/genderDisplay';

interface Photo {
  id: string;
  url: string;
  caption?: string;
}

interface Species {
  id: string;
  commonName: string;
  genus: string;
  species: string;
}

interface Bird {
  id: string;
  bandId: string;
  gender: Gender;
  status: BirdStatus;
  avatarPhotoId?: string;
  photos?: Photo[];
  species?: Species;
  cageNumber: string;
  mutationName?: string;
}

interface HealthTestBirdInfoProps {
  bird: Bird;
  layout?: 'horizontal' | 'vertical';
  showDetails?: boolean;
}

export default function HealthTestBirdInfo({ 
  bird, 
  layout = 'horizontal',
  showDetails = true 
}: HealthTestBirdInfoProps) {
  const getAvatarClass = (gender: Gender) => getGenderAvatarClass(gender);

  const getStatusColor = (status: BirdStatus) => {
    switch (status) {
      case BirdStatus.BREEDING:
        return 'bg-emerald-100 text-emerald-800';
      case BirdStatus.FOR_SALE:
        return 'bg-cyan-100 text-cyan-800';
      case BirdStatus.SOLD:
        return 'bg-violet-100 text-violet-800';
      case BirdStatus.DECEASED:
        return 'bg-rose-100 text-rose-800';
      default:
        return 'bg-violet-100 text-violet-800';
    }
  };

  const getAvatarUrl = () => {
    if (bird.avatarPhotoId) {
      return getPhotoUrl(bird.avatarPhotoId);
    }
    if (bird.photos && bird.photos.length > 0) {
      return getPhotoUrl(bird.photos[0].id);
    }
    return null;
  };

  const avatarUrl = getAvatarUrl();
  const initials = bird.bandId.substring(0, 2).toUpperCase();

  if (layout === 'vertical') {
    return (
      <div className="text-center space-y-2">
        <div className={`h-12 w-12 mx-auto rounded-full border-2 ${getAvatarClass(bird.gender)} overflow-hidden bg-gray-100 flex items-center justify-center`}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={bird.bandId}
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="text-sm font-medium text-slate-600">
              {initials}
            </span>
          )}
        </div>
        <div>
          <div className="flex items-center justify-center space-x-1">
            <span className="font-semibold text-slate-900">{bird.bandId}</span>
            <span className={`text-lg ${getGenderColorClass(bird.gender)}`}>
              {getGenderIcon(bird.gender)}
            </span>
          </div>
          {showDetails && (
            <div className="text-sm text-slate-500 space-y-1">
              {bird.species && (
                <div>{bird.species.commonName}</div>
              )}
              <div>Cage {bird.cageNumber}</div>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(bird.status)}`}>
                {bird.status}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-3">
      <div className={`h-10 w-10 rounded-full border-2 ${getAvatarClass(bird.gender)} overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0`}>
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={bird.bandId}
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="text-sm font-medium text-slate-600">
            {initials}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-2">
          <span className="font-semibold text-slate-900">{bird.bandId}</span>
          <span className={`text-lg font-bold ${getGenderColorClass(bird.gender)}`}>
            {getGenderIcon(bird.gender)}
          </span>
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(bird.status)}`}>
            {bird.status}
          </span>
        </div>
        {showDetails && (
          <div className="text-sm text-slate-500 mt-1">
            <div className="flex items-center space-x-2">
              {bird.species && (
                <span>{bird.species.commonName}</span>
              )}
              {bird.mutationName && (
                <span>• {bird.mutationName}</span>
              )}
              <span>• Cage {bird.cageNumber}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 